import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("sereno_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      localStorage.removeItem("sereno_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("sereno_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("sereno_token", data.token);
    setUser(data.user);

    // If the user was in guest mode, transfer their profile + pending reco
    try {
      const raw = localStorage.getItem("sereno_guest_profile");
      if (raw) {
        const guest = JSON.parse(raw);
        const {
          monthly_income,
          monthly_expenses,
          monthly_savings,
          initial_amount,
          horizon_years,
          risk_profile,
          pending_reco,
        } = guest || {};
        const patch = {};
        if (monthly_income != null) patch.monthly_income = monthly_income;
        if (monthly_expenses != null) patch.monthly_expenses = monthly_expenses;
        if (monthly_savings != null) patch.monthly_savings = monthly_savings;
        if (initial_amount != null) patch.initial_amount = initial_amount;
        if (horizon_years != null) patch.horizon_years = horizon_years;
        if (risk_profile) patch.risk_profile = risk_profile;
        if (Object.keys(patch).length) {
          const { data: p } = await api.patch("/profile", patch);
          setUser(p.user);
        }
        if (pending_reco) {
          await api.post("/recommendations/save", pending_reco);
        }
        localStorage.removeItem("sereno_guest_profile");
      }
    } catch {
      // silent — do not block registration if migration fails
    }
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("sereno_token");
    setUser(null);
  };

  const updateProfile = async (patch) => {
    const { data } = await api.patch("/profile", patch);
    setUser(data.user);
    return data.user;
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, logout, updateProfile, refresh }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
